import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProductionBatch, InspectionModel, Lab } from '../../../model';
import { BatchMeasured3dPlotComponent } from './batch-measured-3d-plot.component';
import { BatchPredictedPlotsComponent } from './batch-predicted-plots.component';
import { BatchRadarPlotComponent } from './batch-radar-plot.component';
import { BatchScanDetailsComponent } from './batch-scan-details.component';
import { BatchDataTableComponent } from './batch-data-table.component';
import { BatchSummaryStatsComponent } from './batch-summary-stats.component';
import { Scan } from '../../../model';

export interface AxisSpans {
  l: number;
  a: number;
  b: number;
}

@Component({
  selector: 'app-batch-analysis',
  standalone: true,
  imports: [
    CommonModule,
    BatchMeasured3dPlotComponent,
    BatchPredictedPlotsComponent,
    BatchRadarPlotComponent,
    BatchScanDetailsComponent,
    BatchDataTableComponent,
    BatchSummaryStatsComponent,
  ],
  templateUrl: './batch-analysis.component.html',
  styleUrl: './batch-analysis.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchAnalysisComponent {
  batch = input.required<ProductionBatch>();
  model = input<InspectionModel | null>(null);
  selectedScan = signal<Scan | null>(null);
  // Bubbled up to po.component so it can pop the scan-dialog. Distinct from
  // the existing in-component sidebar update — clicking a chart point only
  // updates the side panel, while clicking a link in the summary-stats
  // (max/min sequence numbers) is a stronger intent: open the modal.
  scanDialogRequested = output<Scan>();

  sharedAxisSpans = computed<AxisSpans>(() => {
    const b = this.batch();
    if (!b?.scans) return { l: 1, a: 1, b: 1 };

    const span = (values: number[]) => {
      if (!values.length) return 0;
      const min = Math.min(...values);
      const max = Math.max(...values);
      return max - min;
    };

    const measuredLabs: Lab[] = b.scans.filter((s) => s.median).map((s) => s.median!);
    const predictedLabs: Lab[] = b.scans.filter((s) => s.predicted).map((s) => s.predicted!);
    const meshSpan = 8; // Size of tolerance mesh (d=4 => span=8)

    const spanL = Math.max(span(measuredLabs.map((v) => v.l)), span(predictedLabs.map((v) => v.l)), meshSpan);
    const spanA = Math.max(span(measuredLabs.map((v) => v.a)), span(predictedLabs.map((v) => v.a)), meshSpan);
    const spanB = Math.max(span(measuredLabs.map((v) => v.b)), span(predictedLabs.map((v) => v.b)), meshSpan);

    return { l: spanL + 1, a: spanA + 1, b: spanB + 1 };
  });

  onScanSelected(scan: Scan) {
    this.selectedScan.set(scan);
  }

  /** Click on a sequence-number link in the summary-stats block. Update
   *  the sidebar AND ask po.component to open the scan-dialog. */
  onSummaryScanClick(scan: Scan) {
    this.selectedScan.set(scan);
    this.scanDialogRequested.emit(scan);
  }
}
